import { Button } from 'blaze-ui/Button'
import { MobileWizard } from 'components/Wizards/MobileWizards/Layout'
import { ArrowRight, ImageAdd } from 'blaze-ui/icons'
import { multiStep } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/multiStep'
import { StepProps } from 'components/Wizards/MobileWizards/MultiStepMobileWizard/Types'
import { useFormikContext, FormikValues } from 'formik'
import { TopicField } from '../../Fields/TopicField'
import { FilePathField } from '../../Fields/FilePathField'
import { BrandKitField } from '../../Fields/BrandKitField'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { CustomizeButton } from './components/CustomizeButton'
import { useAutocomplete } from 'components/Wizards/DesktopWizards/useAutocomplete'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'
import { useState, useEffect, useRef } from 'react'
import { useWizardSchema } from 'components/Wizards/Wizard/WizardContext'
import { MultiStepSchema, WizardType } from 'components/Wizards/schemas/Types'
import { Field as BaseField } from 'components/Wizards/MobileWizards/Field/Field'
import { Field } from 'components/Wizards/MobileWizards/Form/Field'

interface Props extends StepProps {
  submitLabel?: string
  showAddMedia?: boolean
  isGenerating?: boolean
}

export const RefineDetails = multiStep<Props>(({ schema, submit, getSchemaField, ...props }) => {
  const { submitLabel, showAddMedia, isGenerating } = props
  const { meta } = schema.describe()
  const { values, setValues, setFieldValue, setStatus, initialStatus } = useFormikContext<FormikValues>()
  const topicField = getSchemaField('topic')
  const countField = getSchemaField('count')
  const isBrandKitsEnabled = useFlipperFlag('brand_kits')
  const [isLoading, setIsLoading] = useState(false)
  const hasInitialized = useRef(false)
  const userEditedTopic = useRef(false)

  const wizardSchema = useWizardSchema() as MultiStepSchema<WizardType>

  const { handleGenerate, handleComplete, data } = useAutocomplete({
    schema: wizardSchema,
    values,
    setFieldValue,
    setStatus,
    initialStatus,
    onLoadingChange: setIsLoading,
  })

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true

      if (topicField && !values.topic) {
        handleGenerate({ fields: ['topic', 'filename'] })
      }
    }
  }, [topicField, values.topic, handleGenerate])

  const handleMediaPress = async () => {
    setFieldValue('showAddMedia', true)
    submit()
  }

  const handleBrandKitChange = (brandKitId: number) => {
    if (userEditedTopic.current) return

    handleGenerate({ fields: ['topic', 'filename'], newCompletedFields: { brand_kit_id: brandKitId } })
  }

  const handleTopicKeyDown = () => {
    userEditedTopic.current = true
  }

  return (
    <>
      <PollFieldsetCompletion fieldsetCompletionId={data?.fieldset_completion.id} onComplete={handleComplete} />
      <MobileWizard.Content>
        <MobileWizard.Section>
          <MobileWizard.Title>{meta.title}</MobileWizard.Title>
        </MobileWizard.Section>
        {topicField && (
          <BaseField name="topic" label={topicField.describe().meta.label}>
            <TopicField schema={topicField} isLoading={isLoading} onKeyDown={handleTopicKeyDown} />
          </BaseField>
        )}
        {countField && <Field name="count" schema={countField} />}
        {isBrandKitsEnabled && (
          <BaseField label="Pick a Brand Kit">
            <BrandKitField onBrandKitChange={handleBrandKitChange} />
          </BaseField>
        )}
        <FilePathField label="Name your content and select location" isLoading={isLoading} />
        <CustomizeButton schema={schema} initialValues={values} onSubmit={setValues} />
      </MobileWizard.Content>
      <MobileWizard.ButtonFooter>
        {showAddMedia && (
          <Button
            size="lg"
            variant="tertiary"
            frontIcon={ImageAdd}
            onPress={handleMediaPress}
            fullWidth
            isDisabled={isLoading || isGenerating}
          >
            Add Image
          </Button>
        )}
        <Button
          size="lg"
          variant="primary"
          endIcon={ArrowRight}
          fullWidth
          onPress={submit}
          aria-label={submitLabel}
          isDisabled={isLoading || isGenerating}
        >
          {submitLabel || 'Continue'}
        </Button>
      </MobileWizard.ButtonFooter>
    </>
  )
})
